// types/core.js
// Shared type definitions and interfaces for the storage system

/**
 * Origin of a record - whether it came from requirements engine or manual entry
 */
export const ORIGINS = {
  REQUIREMENTS: 'requirements',
  MANUAL: 'manual'
};

/**
 * Collection names used throughout the app
 */
export const COLLECTIONS = {
  EQUIPMENT: 'equipment',
  PROCEDURES: 'procedures', 
  DRILLS: 'drills',
  TRAINING: 'training',
  INSPECTIONS: 'inspections',
  SCHEDULE: 'schedule',
  ACTIVITY_LOG: 'activityLog',
  OPS_QUEUE: 'opsQueue',
  META: 'meta',
  CREW: 'crew',
  MAINTENANCE: 'maintenance',
  DEFECTS: 'defects',
  INCIDENTS: 'incidents',
  HOURS_OF_REST: 'hoursOfRest'
};

/**
 * Base interface for all records
 * All records must have these fields
 */
export function createBaseRecord(data = {}) {
  return {
    id: data.id || null, // Will be generated if not provided
    vesselId: data.vesselId || null, // Required
    origin: data.origin || ORIGINS.MANUAL,
    createdAt: data.createdAt || Date.now(),
    updatedAt: data.updatedAt || Date.now(),
    requirementsRef: data.requirementsRef || null, // { ruleId, versionHash }
    ...data
  };
}

/**
 * Equipment item structure
 */
export function createEquipmentItem(data = {}) {
  return createBaseRecord({
    name: data.name || '',
    class: data.class || '',
    maintenance: data.maintenance || [],
    standards: data.standards || [],
    evidence: data.evidence || [],
    notes: data.notes || [],
    serial: data.serial || null,
    qrCode: data.qrCode || null,
    location: data.location || null,
    manufacturer: data.manufacturer || null,
    model: data.model || null,
    installDate: data.installDate || null,
    lastService: data.lastService || null,
    nextService: data.nextService || null,
    ...data
  });
}

/**
 * Procedure template structure
 */
export function createProcedureTemplate(data = {}) {
  return createBaseRecord({
    title: data.title || '',
    locked: data.locked || false,
    steps: data.steps || [],
    references: data.references || [],
    category: data.category || '',
    scope: data.scope || '',
    approvedBy: data.approvedBy || null,
    version: data.version || '1.0',
    ...data
  });
}

/**
 * Drill type structure
 */
export function createDrillType(data = {}) {
  return createBaseRecord({
    type: data.type || '',
    frequency: data.frequency || '',
    participants: data.participants || [],
    evidence: data.evidence || [],
    description: data.description || '',
    duration: data.duration || null,
    objectives: data.objectives || [],
    ...data
  });
}

/**
 * Training requirement structure
 */
export function createTrainingReq(data = {}) {
  return createBaseRecord({
    role: data.role || '',
    topic: data.topic || '',
    refresh: data.refresh || '',
    evidence: data.evidence || [],
    description: data.description || '',
    provider: data.provider || null,
    certification: data.certification || null,
    ...data
  });
}

/**
 * Inspection template structure
 */
export function createInspectionTemplate(data = {}) {
  return createBaseRecord({
    name: data.name || '',
    frequency: data.frequency || '',
    checklist: data.checklist || [],
    equipment: data.equipment || [],
    responsible: data.responsible || '',
    documentation: data.documentation || [],
    ...data
  });
}

/**
 * Operations queue item structure
 */
export function createOpQueueItem(data = {}) {
  return {
    id: data.id || null,
    ts: data.ts || Date.now(),
    vesselId: data.vesselId || null,
    collection: data.collection || null,
    action: data.action || null, // 'upsert' | 'remove'
    recordId: data.recordId || null,
    payloadHash: data.payloadHash || null,
    payload: data.payload || null,
    synced: data.synced || false,
    syncAttempts: data.syncAttempts || 0,
    lastSyncAttempt: data.lastSyncAttempt || null,
    error: data.error || null,
    ...data
  };
}

/**
 * Requirements reference structure
 */
export function createRequirementsRef(ruleId, versionHash) {
  return {
    ruleId: ruleId || null,
    versionHash: versionHash || null,
    generatedAt: Date.now()
  };
}

/**
 * Validation helpers
 */
export function validateBaseRecord(record) {
  const errors = [];
  
  if (!record.id) errors.push('ID is required');
  if (!record.vesselId) errors.push('vesselId is required');
  if (!Object.values(ORIGINS).includes(record.origin)) {
    errors.push(`Invalid origin: ${record.origin}`);
  }
  if (!record.createdAt || !record.updatedAt) {
    errors.push('createdAt and updatedAt are required');
  }
  
  return errors;
}

export function validateCollection(collectionName) {
  return Object.values(COLLECTIONS).includes(collectionName);
}

/**
 * Helper to normalize record before storage
 */
export function normalizeRecord(record, collectionType) {
  const baseRecord = createBaseRecord(record);
  
  switch (collectionType) {
    case COLLECTIONS.EQUIPMENT:
      return createEquipmentItem(baseRecord);
    case COLLECTIONS.PROCEDURES:
      return createProcedureTemplate(baseRecord);
    case COLLECTIONS.DRILLS:
      return createDrillType(baseRecord);
    case COLLECTIONS.TRAINING:
      return createTrainingReq(baseRecord);
    case COLLECTIONS.INSPECTIONS:
      return createInspectionTemplate(baseRecord);
    default:
      return baseRecord;
  }
}